/**
 * Curated seed / chapter default images (AI-generated thematic art).
 * Files live in `apps/frontend/public/seed-images/` and are served as `/seed-images/…`.
 * Every photo ID is unique across the entire catalog — no reuse.
 * Themes: worship, scripture, Word of God, prayer, discipline, community, love, sacrifice.
 */

const seedImage = (id: number) => `/seed-images/${id}.jpeg`

const portrait = (id: number) => seedImage(id)

export const SEED_IMAGES = {
    /** Main landing page hero carousel (seeded to hero_slides table) */
    heroSlides: {
        // worship — congregation with raised hands in prayer
        worship: seedImage(13899699),
        // scripture — open Bible / study
        scripture: seedImage(846847),
        // dublin — city / chapter presence
        dublin: seedImage(235593),
        // cork — coastal city character
        cork: seedImage(912050),
        // motivation / spiritual growth — upward journey
        growth: seedImage(2228570),
        // worship experience — candlelit devotion
        experience: seedImage(846870),
    },
    chapters: {
        dublin: {
            // community city church (distinct from homepage Dublin slide)
            hero: seedImage(289737),
            gallery: [
                seedImage(13899700), // worship — group prayer
                seedImage(3184418), // community — friends together
                seedImage(846855), // scripture / devotion
                seedImage(846860), // candlelit prayer
                seedImage(1128317), // family / community
                seedImage(4473409), // celebration / joy
                seedImage(1563256), // gathering outdoors
                seedImage(1769408), // fellowship meal
                seedImage(3807755), // worship hands
                seedImage(3771106), // church interior light
            ],
            eventConvention: seedImage(3182812), // gathering — conference hall
            eventSunday: seedImage(3778680), // worship — church service
            serviceSunday: seedImage(1626464), // praise — congregation
            serviceMidweek: seedImage(3184296), // prayer — small group
            birthdayPortraits: [
                portrait(774909),
                portrait(1181690),
                portrait(415829),
                portrait(733872),
                portrait(1024311),
                portrait(1043471),
                portrait(1181681),
                portrait(1308885),
                portrait(1438761),
                portrait(1516680),
                portrait(1542085),
                portrait(1681010),
            ],
        },
        cork: {
            hero: seedImage(206359), // chapter — coastal / coastal light
            gallery: [
                seedImage(169190), // community — outdoor fellowship
                seedImage(8437045), // love / community meal
                seedImage(3861964), // coastal walk / community
                seedImage(3184291), // team huddle
                seedImage(2379004), // city energy
                seedImage(1858175), // warm gathering
            ],
            eventSunday: seedImage(1181396), // worship gathering
            serviceSunday: seedImage(3184360), // community circle
            serviceMidweek: seedImage(3183153), // teamwork / fellowship
            birthdayPortraits: [
                portrait(1181519),
                portrait(2613260),
                portrait(2726111),
                portrait(3785079),
                portrait(1130626),
                portrait(1239288),
                portrait(1462980),
                portrait(1587009),
            ],
        },
        drogheda: {
            hero: seedImage(2901209), // countryside / Boyne Valley feel
            gallery: [seedImage(3184292)], // community huddle
            eventLaunch: seedImage(13899701), // prayer — hands raised
            birthdayPortraits: [portrait(1222271)],
        },
        kerry: {
            hero: seedImage(267392), // historic city skyline
            gallery: [seedImage(3183150)], // leadership / vision circle
            eventVision: seedImage(4050290), // motivation — looking ahead
            birthdayPortraits: [portrait(1239291)],
        },
        sligo: {
            hero: seedImage(267667), // wild landscape / Atlantic
            gallery: [seedImage(247599)], // mountain path / pilgrimage
            eventConnect: seedImage(3184423), // community conversation
            birthdayPortraits: [portrait(1181717)],
        },
        waterford: {
            hero: seedImage(2379005), // city by the water
            gallery: [seedImage(2774570)], // cathedral / sacred space
            eventPrayer: seedImage(372326), // prayer / kneeling devotion
            birthdayPortraits: [portrait(1181695)],
        },
    },

    media: {
        worshipGathering: seedImage(289740), // worship — hands lifted
        leadershipConference: seedImage(2736386), // sacrifice / service — helping hand
    },
    // prayer / contemplation for weekly declaration
    weeklyDeclaration: seedImage(225390),
} as const

export type SeedChapterSlug = keyof typeof SEED_IMAGES.chapters

/** Collect every numeric photo ID used in SEED_IMAGES (for uniqueness checks). */
export function collectSeedImageIds(root: unknown = SEED_IMAGES): number[] {
    const ids: number[] = []
    const walk = (value: unknown) => {
        if (typeof value === "string") {
            const match =
                value.match(/\/seed-images\/(\d+)\.jpe?g$/i) ??
                value.match(/\/photos\/(\d+)\//)
            if (match) ids.push(Number(match[1]))
            return
        }
        if (Array.isArray(value)) {
            value.forEach(walk)
            return
        }
        if (value && typeof value === "object") {
            Object.values(value).forEach(walk)
        }
    }
    walk(root)
    return ids
}

export function assertUniqueSeedImages(root: unknown = SEED_IMAGES) {
    const ids = collectSeedImageIds(root)
    const seen = new Set<number>()
    const dupes: number[] = []
    for (const id of ids) {
        if (seen.has(id)) dupes.push(id)
        seen.add(id)
    }
    if (dupes.length > 0) {
        throw new Error(`SEED_IMAGES has duplicate photo IDs: ${[...new Set(dupes)].join(", ")}`)
    }
}
